import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  Upload, 
  LogOut, 
  Calendar,
  Users,
  MapPin,
  RefreshCw,
  Star,
  Copy
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useNavigate } from 'react-router-dom'
import { useTours, type Tour, getLocalizedText, getLocalizedNumber } from '../hooks/useTours'
import { TourForm } from '../components/Admin/TourForm'
import { NotificationContainer, type NotificationData } from '../components/Admin/Notification'
import { TourService, type CreateTourData } from '../services/tourService'
import { calculateTourStatus, getStatusLabel } from '../utils/tourStatus'
import { useHotels, type Hotel, getLocalizedText as getHotelLocalizedText } from '../hooks/useHotels'
import { HotelForm } from '../components/Admin/HotelForm'
import { HotelService, type CreateHotelData } from '../services/hotelService'
import { useTestimonials } from '../hooks/useTestimonials'
import { type Testimonial } from '../services/testimonialService'
import { TestimonialForm } from '../components/Admin/TestimonialForm'
import { TestimonialService, type CreateTestimonialData } from '../services/testimonialService'

export function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'tours' | 'hotels' | 'testimonials'>('tours')
  const [tours, setTours] = useState<Tour[]>([])
  const [editingTour, setEditingTour] = useState<Tour | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [notifications, setNotifications] = useState<NotificationData[]>([])
  const { logout } = useAuth()
  const { language } = useLanguage()
  const navigate = useNavigate()
  const { tours: fetchedTours, loading, refetch } = useTours()
  
  // Hotels state
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null)
  const [isHotelFormOpen, setIsHotelFormOpen] = useState(false)
  const [isSavingHotel, setIsSavingHotel] = useState(false)
  const [showDeletedHotels, setShowDeletedHotels] = useState(false)
  const { hotels: fetchedHotels, loading: hotelsLoading, refetch: refetchHotels } = useHotels()
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null)
  const [isTestimonialFormOpen, setIsTestimonialFormOpen] = useState(false)
  const [isSavingTestimonial, setIsSavingTestimonial] = useState(false)
  const [showDeletedTestimonials, setShowDeletedTestimonials] = useState(false)
  const { testimonials: fetchedTestimonials, loading: testimonialsLoading, refetch: refetchTestimonials } = useTestimonials(showDeletedTestimonials)
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    item: Tour | Hotel | Testimonial | null
    type: 'tour' | 'hotel' | 'testimonial' | null
  }>({
    show: false,
    item: null,
    type: null
  })

  useEffect(() => {
    if (fetchedTours.length > 0) {
      // Filter tours based on showDeleted preference
      const filteredTours = showDeleted 
        ? fetchedTours // Show all tours including deleted
        : fetchedTours.filter(tour => !tour.deletedAt) // Show only non-deleted tours
      
      
      setTours(filteredTours)
    }
  }, [fetchedTours, showDeleted])

  useEffect(() => {
    if (fetchedHotels.length > 0) {
      const filteredHotels = showDeletedHotels
        ? fetchedHotels
        : fetchedHotels.filter(hotel => !hotel.deletedAt)
      setHotels(filteredHotels)
    }
  }, [fetchedHotels, showDeletedHotels])

  useEffect(() => {
    if (fetchedTestimonials.length > 0) {
      const filteredTestimonials = showDeletedTestimonials
        ? fetchedTestimonials
        : fetchedTestimonials.filter(testimonial => !testimonial.deleted_at)
      setTestimonials(filteredTestimonials)
    }
  }, [fetchedTestimonials, showDeletedTestimonials])

  const addNotification = (notification: Omit<NotificationData, 'id'>) => {
    const id = Date.now().toString()
    setNotifications(prev => [...prev, { ...notification, id }])
  }

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  const handleLogout = () => {
    logout()
    navigate('/admin/login')
  }

  const handleEdit = (tour: Tour) => {
    setEditingTour(tour)
    setIsFormOpen(true)
  }

  const handleAddNew = () => {
    setEditingTour(null)
    setIsFormOpen(true)
  }

  const handleDuplicate = (tour: Tour) => {
    // Create a duplicate tour with a new ID and "Copy" prefix in titles
    const duplicatedTour: Tour = {
      ...tour,
      id: crypto.randomUUID(), // New ID so it's treated as a new tour
      title: {
        en: `Copy of ${tour.title.en}`,
        ar: `نسخة من ${tour.title.ar}`,
        ur: `${tour.title.ur} کی نقل`,
        fa: `کپی از ${tour.title.fa}`
      },
      deletedAt: undefined // Remove deletedAt if present
    }
    setEditingTour(duplicatedTour)
    setIsFormOpen(true)
  }

  const handleSave = async (tourData: CreateTourData) => {
    setIsSaving(true)
    
    try {
      let result
      
      // Check if editingTour exists and has an ID that exists in the current tours list
      const isExistingTour = editingTour && fetchedTours.some(t => t.id === editingTour.id)
      
      if (isExistingTour && editingTour) {
        // Update existing tour
        result = await TourService.updateTour({ id: editingTour.id, ...tourData })
      } else {
        // Create new tour (either new or duplicate)
        result = await TourService.createTour(tourData)
      }
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: editingTour ? 'Tour Updated' : 'Tour Created',
          message: `Tour "${tourData.title_en}" has been ${editingTour ? 'updated' : 'created'} successfully.`
        })
        
        // Refresh tours data
        if (refetch) {
          await refetch()
        }
        
        setIsFormOpen(false)
        setEditingTour(null)
      } else {
        addNotification({
          type: 'error',
          title: 'Error',
          message: result.error || 'Failed to save tour'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'An unexpected error occurred while saving the tour'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = (tour: Tour) => {
    setDeleteConfirm({
      show: true,
      item: tour,
      type: 'tour'
    })
  }

  const handleSoftDelete = async () => {
    if (!deleteConfirm.item || !deleteConfirm.type) return

    try {
      let result
      let itemName = ''

      if (deleteConfirm.type === 'tour') {
        const tour = deleteConfirm.item as Tour
        itemName = getLocalizedText(tour.title, language)
        result = await TourService.deleteTour(tour.id)
      } else if (deleteConfirm.type === 'hotel') {
        const hotel = deleteConfirm.item as Hotel
        itemName = getHotelLocalizedText(hotel.name, language)
        result = await HotelService.deleteHotel(hotel.id)
      } else {
        const testimonial = deleteConfirm.item as Testimonial
        itemName = testimonial.author_name || 'Testimonial'
        result = await TestimonialService.deleteTestimonial(testimonial.id)
      }

      if (result.success) {
        addNotification({
          type: 'success',
          title: deleteConfirm.type === 'tour' ? 'Tour Deleted' : deleteConfirm.type === 'hotel' ? 'Hotel Deleted' : 'Testimonial Deleted',
          message: `${deleteConfirm.type === 'tour' ? 'Tour' : deleteConfirm.type === 'hotel' ? 'Hotel' : 'Testimonial'} "${itemName}" has been soft deleted successfully. You can restore it later.`
        })

        // Refresh data
        if (deleteConfirm.type === 'tour' && refetch) {
          await refetch()
        } else if (deleteConfirm.type === 'hotel' && refetchHotels) {
          await refetchHotels()
        } else if (deleteConfirm.type === 'testimonial' && refetchTestimonials) {
          await refetchTestimonials()
        }

        setDeleteConfirm({ show: false, item: null, type: null })
      } else {
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: result.error || `Failed to delete ${deleteConfirm.type || 'item'}`
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: `An unexpected error occurred while deleting the ${deleteConfirm.type}`
      })
    }
  }

  const handlePermanentDelete = async () => {
    if (!deleteConfirm.item || !deleteConfirm.type) return

    try {
      let result
      let itemName = ''
      let imageUrls: string[] = []

      if (deleteConfirm.type === 'tour') {
        const tour = deleteConfirm.item as Tour
        itemName = getLocalizedText(tour.title, language)
        // Collect all image URLs
        imageUrls = [
          tour.image,
          ...(tour.images || []),
          ...(tour.memories || [])
        ].filter(Boolean) as string[]
        result = await TourService.permanentlyDeleteTour(tour.id, imageUrls)
      } else if (deleteConfirm.type === 'hotel') {
        const hotel = deleteConfirm.item as Hotel
        itemName = getHotelLocalizedText(hotel.name, language)
        // Collect all image URLs
        imageUrls = (hotel.images || []).filter(Boolean) as string[]
        result = await HotelService.permanentlyDeleteHotel(hotel.id, imageUrls)
      } else {
        const testimonial = deleteConfirm.item as Testimonial
        itemName = testimonial.author_name || 'Testimonial'
        const contentUrl = testimonial.type === 'image' || testimonial.type === 'video' ? testimonial.content : undefined
        result = await TestimonialService.permanentlyDeleteTestimonial(testimonial.id, contentUrl)
      }

      if (result.success) {
        addNotification({
          type: 'success',
          title: deleteConfirm.type === 'tour' ? 'Tour Permanently Deleted' : deleteConfirm.type === 'hotel' ? 'Hotel Permanently Deleted' : 'Testimonial Permanently Deleted',
          message: `${deleteConfirm.type === 'tour' ? 'Tour' : deleteConfirm.type === 'hotel' ? 'Hotel' : 'Testimonial'} "${itemName}" has been permanently deleted. This action cannot be undone.`
        })

        // Refresh data
        if (deleteConfirm.type === 'tour' && refetch) {
          await refetch()
        } else if (deleteConfirm.type === 'hotel' && refetchHotels) {
          await refetchHotels()
        } else if (deleteConfirm.type === 'testimonial' && refetchTestimonials) {
          await refetchTestimonials()
        }

        setDeleteConfirm({ show: false, item: null, type: null })
      } else {
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: result.error || `Failed to permanently delete ${deleteConfirm.type || 'item'}`
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: `An unexpected error occurred while permanently deleting the ${deleteConfirm.type}`
      })
    }
  }

  const handleRestore = async (tour: Tour) => {
    try {
      const result = await TourService.restoreTour(tour.id)
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: 'Tour Restored',
          message: `Tour "${getLocalizedText(tour.title, language)}" has been restored successfully.`
        })
        
        // Refresh tours data
        if (refetch) {
          await refetch()
        }
      } else {
        addNotification({
          type: 'error',
          title: 'Restore Failed',
          message: result.error || 'Failed to restore tour'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Restore Failed',
        message: 'An unexpected error occurred while restoring the tour'
      })
    }
  }


  const handleExport = () => {
    const dataStr = JSON.stringify({ tours }, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'tours.json'
    link.click()
    URL.revokeObjectURL(url)
    
    addNotification({
      type: 'success',
      title: 'Export Successful',
      message: 'Tours data has been exported successfully.'
    })
  }

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string)
        const importedTours = importedData.tours || importedData
        
        if (Array.isArray(importedTours)) {
        setTours(importedTours)
          addNotification({
            type: 'success',
            title: 'Import Successful',
            message: `${importedTours.length} tours have been imported successfully.`
          })
        } else {
          throw new Error('Invalid data format')
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Import Failed',
          message: 'Error importing file. Please check the JSON format.'
        })
      }
    }
    reader.readAsText(file)
  }

  const handleRefresh = async () => {
    if (refetch) {
      setIsRefreshing(true)
      try {
        await refetch()
        addNotification({
          type: 'success',
          title: 'Data Refreshed',
          message: 'Tours data has been refreshed successfully.'
        })
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Refresh Failed',
          message: 'Failed to refresh tours data'
        })
      } finally {
        setIsRefreshing(false)
      }
    }
  }

  // Hotel handlers
  const handleEditHotel = (hotel: Hotel) => {
    setEditingHotel(hotel)
    setIsHotelFormOpen(true)
  }

  const handleAddNewHotel = () => {
    setEditingHotel(null)
    setIsHotelFormOpen(true)
  }

  const handleSaveHotel = async (hotelData: CreateHotelData) => {
    setIsSavingHotel(true)
    
    try {
      let result
      
      if (editingHotel) {
        result = await HotelService.updateHotel({ id: editingHotel.id, ...hotelData })
      } else {
        result = await HotelService.createHotel(hotelData)
      }
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: editingHotel ? 'Hotel Updated' : 'Hotel Created',
          message: `Hotel "${hotelData.name_en}" has been ${editingHotel ? 'updated' : 'created'} successfully.`
        })
        
        if (refetchHotels) {
          await refetchHotels()
        }
        
        setIsHotelFormOpen(false)
        setEditingHotel(null)
      } else {
        addNotification({
          type: 'error',
          title: 'Error',
          message: result.error || 'Failed to save hotel'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'An unexpected error occurred while saving the hotel'
      })
    } finally {
      setIsSavingHotel(false)
    }
  }

  const handleDeleteHotel = (hotel: Hotel) => {
    setDeleteConfirm({
      show: true,
      item: hotel,
      type: 'hotel'
    })
  }

  const handleRestoreHotel = async (hotel: Hotel) => {
    try {
      const result = await HotelService.restoreHotel(hotel.id)
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: 'Hotel Restored',
          message: `Hotel "${getHotelLocalizedText(hotel.name, language)}" has been restored successfully.`
        })
        
        if (refetchHotels) {
          await refetchHotels()
        }
      } else {
        addNotification({
          type: 'error',
          title: 'Restore Failed',
          message: result.error || 'Failed to restore hotel'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Restore Failed',
        message: 'An unexpected error occurred while restoring the hotel'
      })
    }
  }

  // Testimonial handlers
  const handleEditTestimonial = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial)
    setIsTestimonialFormOpen(true)
  }

  const handleAddNewTestimonial = () => {
    setEditingTestimonial(null)
    setIsTestimonialFormOpen(true)
  }

  const handleSaveTestimonial = async (testimonialData: CreateTestimonialData) => {
    setIsSavingTestimonial(true)
    
    try {
      let result
      
      if (editingTestimonial) {
        result = await TestimonialService.updateTestimonial({ id: editingTestimonial.id, ...testimonialData })
      } else {
        result = await TestimonialService.createTestimonial(testimonialData)
      }
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: editingTestimonial ? 'Testimonial Updated' : 'Testimonial Created',
          message: `Testimonial has been ${editingTestimonial ? 'updated' : 'created'} successfully.`
        })
        
        if (refetchTestimonials) {
          await refetchTestimonials()
        }
        
        setIsTestimonialFormOpen(false)
        setEditingTestimonial(null)
      } else {
        addNotification({
          type: 'error',
          title: 'Error',
          message: result.error || 'Failed to save testimonial'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'An unexpected error occurred while saving the testimonial'
      })
    } finally {
      setIsSavingTestimonial(false)
    }
  }

  const handleDeleteTestimonial = (testimonial: Testimonial) => {
    setDeleteConfirm({
      show: true,
      item: testimonial,
      type: 'testimonial'
    })
  }

  const handleRestoreTestimonial = async (testimonial: Testimonial) => {
    try {
      const result = await TestimonialService.restoreTestimonial(testimonial.id)
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: 'Testimonial Restored',
          message: `Testimonial has been restored successfully.`
        })
        
        if (refetchTestimonials) {
          await refetchTestimonials()
        }
      } else {
        addNotification({
          type: 'error',
          title: 'Restore Failed',
          message: result.error || 'Failed to restore testimonial'
        })
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Restore Failed',
        message: 'An unexpected error occurred while restoring the testimonial'
      })
    }
  }

  const getStatusColor = (status: 'upcoming' | 'ongoing' | 'past') => {
    switch (status) {
      case 'ongoing':
        return 'bg-primary text-white'
      case 'upcoming':
        return 'bg-blue-500 text-white'
      case 'past':
        return 'bg-gray-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  if (loading || hotelsLoading || testimonialsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pt-16">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage your pilgrimage tours</p>
            </div>
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </motion.button>
             
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('tours')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'tours'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Tours
          </button>
          <button
            onClick={() => setActiveTab('hotels')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'hotels'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Hotels
          </button>
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'testimonials'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Testimonials
          </button>
        </div>

        {/* Tours Tab */}
        {activeTab === 'tours' && (
          <>
            {/* Admin Controls */}
            <div className="flex items-center justify-between mb-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddNew}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-5 w-5" />
                Add New Tour
              </motion.button>

          {/* Deleted Tours Filter */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="text-sm font-medium text-foreground">
                Show Deleted Tours
              </span>
            </label>
            {showDeleted && (
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                  {tours.filter(tour => tour.deletedAt).length} Deleted
                </span>
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                  {tours.filter(tour => !tour.deletedAt).length} Active
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tours Grid */}
        {tours.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              {showDeleted ? 'No deleted tours found' : 'No active tours found'}
            </div>
            {showDeleted && (
              <button
                onClick={() => setShowDeleted(false)}
                className="text-primary hover:text-primary/80 text-sm"
              >
                Show active tours instead
              </button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((tour, index) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Tour Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src={tour.image}
                  alt={getLocalizedText(tour.title, language)}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-card-foreground mb-2">
                      {getLocalizedText(tour.title, language)}
                    </h3>
                    <div className="flex items-center gap-2">
                      <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(calculateTourStatus(tour.departureDate, tour.returnDate))}`}>
                        {getStatusLabel(calculateTourStatus(tour.departureDate, tour.returnDate))}
                      </div>
                      {tour.deletedAt && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                          Deleted
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-primary price">{getLocalizedNumber(tour.price, language)}</div>
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {new Date(tour.departureDate).toLocaleDateString()} - {new Date(tour.returnDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {tour.route.length} destinations
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEdit(tour)}
                    disabled={isSaving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDuplicate(tour)}
                    disabled={isSaving}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Duplicate Tour"
                  >
                    <Copy className="h-4 w-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDelete(tour)}
                    disabled={isSaving}
                    className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
          </div>
        )}
          </>
        )}

        {/* Hotels Tab */}
        {activeTab === 'hotels' && (
          <>
            {/* Admin Controls */}
            <div className="flex items-center justify-between mb-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddNewHotel}
                disabled={isSavingHotel}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-5 w-5" />
                Add New Hotel
              </motion.button>

              {/* Deleted Hotels Filter */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDeletedHotels}
                    onChange={(e) => setShowDeletedHotels(e.target.checked)}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Show Deleted Hotels
                  </span>
                </label>
                {showDeletedHotels && (
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                      {hotels.filter(hotel => hotel.deletedAt).length} Deleted
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                      {hotels.filter(hotel => !hotel.deletedAt).length} Active
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Hotels Grid */}
            {hotels.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-4">
                  {showDeletedHotels ? 'No deleted hotels found' : 'No active hotels found'}
                </div>
                {showDeletedHotels && (
                  <button
                    onClick={() => setShowDeletedHotels(false)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Show active hotels instead
                  </button>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hotels.map((hotel, index) => (
                  <motion.div
                    key={hotel.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Hotel Image */}
                    <div className="h-48 overflow-hidden">
                      {hotel.images && hotel.images.length > 0 ? (
                        <img
                          src={hotel.images[0]}
                          alt={getHotelLocalizedText(hotel.name, language)}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-secondary flex items-center justify-center">
                          <MapPin className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-card-foreground mb-2">
                            {getHotelLocalizedText(hotel.name, language)}
                          </h3>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span className="text-sm">{getHotelLocalizedText(hotel.city, language)}</span>
                          </div>
                          {hotel.deletedAt && (
                            <span className="mt-2 inline-block px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                              Deleted
                            </span>
                          )}
                        </div>
                        {hotel.rating && (
                          <div className="flex items-center gap-1 bg-primary/10 px-3 py-1 rounded-lg">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="font-bold">{hotel.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleEditHotel(hotel)}
                          disabled={isSavingHotel}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteHotel(hotel)}
                          disabled={isSavingHotel}
                          className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <>
            {/* Admin Controls */}
            <div className="flex items-center justify-between mb-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddNewTestimonial}
                disabled={isSavingTestimonial}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-5 w-5" />
                Add New Testimonial
              </motion.button>

              {/* Deleted Testimonials Filter */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDeletedTestimonials}
                    onChange={(e) => setShowDeletedTestimonials(e.target.checked)}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Show Deleted Testimonials
                  </span>
                </label>
                {showDeletedTestimonials && (
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                      {testimonials.filter(t => t.deleted_at).length} Deleted
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                      {testimonials.filter(t => !t.deleted_at).length} Active
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonials Grid */}
            {testimonials.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-4">
                  {showDeletedTestimonials ? 'No deleted testimonials found' : 'No active testimonials found'}
                </div>
                {showDeletedTestimonials && (
                  <button
                    onClick={() => setShowDeletedTestimonials(false)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Show active testimonials instead
                  </button>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize mb-2">
                            {testimonial.type}
                          </div>
                          {testimonial.deleted_at && (
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                              Deleted
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content Preview */}
                      <div className="mb-4">
                        {testimonial.type === 'text' && (
                          <p className="text-sm text-card-foreground line-clamp-3">{testimonial.content}</p>
                        )}
                        {testimonial.type === 'image' && (
                          <img
                            src={testimonial.content}
                            alt="Testimonial"
                            className="w-full h-32 object-cover rounded-lg"
                          />
                        )}
                        {testimonial.type === 'youtube' && (
                          <div className="text-sm text-muted-foreground">YouTube: {testimonial.content.substring(0, 50)}...</div>
                        )}
                        {testimonial.type === 'instagram' && (
                          <div className="text-sm text-muted-foreground">Instagram: {testimonial.content.substring(0, 50)}...</div>
                        )}
                        {testimonial.type === 'video' && (
                          <div className="text-sm text-muted-foreground">Video: {testimonial.content.substring(0, 50)}...</div>
                        )}
                      </div>

                      {/* Author Info */}
                      {(testimonial.author_name || testimonial.author_location) && (
                        <div className="mb-4 text-sm">
                          {testimonial.author_name && (
                            <p className="font-semibold text-card-foreground">{testimonial.author_name}</p>
                          )}
                          {testimonial.author_location && (
                            <p className="text-muted-foreground">{testimonial.author_location}</p>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleEditTestimonial(testimonial)}
                          disabled={isSavingTestimonial}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteTestimonial(testimonial)}
                          disabled={isSavingTestimonial}
                          className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tour Form Modal */}
      <TourForm
          tour={editingTour}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
            setEditingTour(null)
        }}
        onSave={handleSave}
        onDelete={async (tourId: string) => {
          const tour = tours.find(t => t.id === tourId)
          if (tour) {
            await handleDelete(tour)
          }
        }}
        onRestore={async (tourId: string) => {
          const tour = tours.find(t => t.id === tourId)
          if (tour) {
            await handleRestore(tour)
          }
        }}
        isLoading={isSaving}
      />


      {/* Hotel Form Modal */}
      <HotelForm
        hotel={editingHotel}
        isOpen={isHotelFormOpen}
        onClose={() => {
          setIsHotelFormOpen(false)
          setEditingHotel(null)
        }}
        onSave={handleSaveHotel}
        onDelete={async (hotelId: string) => {
          const hotel = hotels.find(h => h.id === hotelId)
          if (hotel) {
            await handleDeleteHotel(hotel)
          }
        }}
        onRestore={async (hotelId: string) => {
          const hotel = hotels.find(h => h.id === hotelId)
          if (hotel) {
            await handleRestoreHotel(hotel)
          }
        }}
        isLoading={isSavingHotel}
      />

      {/* Testimonial Form Modal */}
      <TestimonialForm
        testimonial={editingTestimonial}
        isOpen={isTestimonialFormOpen}
        onClose={() => {
          setIsTestimonialFormOpen(false)
          setEditingTestimonial(null)
        }}
        onSave={handleSaveTestimonial}
        onDelete={async (testimonialId: string) => {
          const testimonial = testimonials.find(t => t.id === testimonialId)
          if (testimonial) {
            await handleDeleteTestimonial(testimonial)
          }
        }}
        onRestore={async (testimonialId: string) => {
          const testimonial = testimonials.find(t => t.id === testimonialId)
          if (testimonial) {
            await handleRestoreTestimonial(testimonial)
          }
        }}
        isLoading={isSavingTestimonial}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && deleteConfirm.item && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <h3 className="text-xl font-bold text-foreground mb-2">
              Delete {deleteConfirm.type === 'tour' ? 'Tour' : deleteConfirm.type === 'hotel' ? 'Hotel' : 'Testimonial'}?
            </h3>
            <p className="text-muted-foreground mb-6">
              {deleteConfirm.type === 'tour' 
                ? `Are you sure you want to delete "${getLocalizedText((deleteConfirm.item as Tour).title, language)}"?`
                : deleteConfirm.type === 'hotel'
                ? `Are you sure you want to delete "${getHotelLocalizedText((deleteConfirm.item as Hotel).name, language)}"?`
                : `Are you sure you want to delete this testimonial?`
              }
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <p className="text-sm font-medium text-foreground mb-2">Soft Delete (Recommended)</p>
                <p className="text-xs text-muted-foreground">
                  Marks the item as deleted but keeps it in the database. You can restore it later.
                </p>
              </div>
              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <p className="text-sm font-medium text-destructive mb-2">Permanent Delete (Irreversible)</p>
                <p className="text-xs text-muted-foreground">
                  Permanently removes the item and all associated images. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setDeleteConfirm({ show: false, item: null, type: null })}
                className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSoftDelete}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Soft Delete
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePermanentDelete}
                className="flex-1 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
              >
                Delete Permanently
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Notifications */}
      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  )
}

